import React, { useMemo } from "react";
import {
  Platform,
  type StyleProp,
  StyleSheet,
  Text,
  type TextProps,
  type TextStyle,
} from "react-native";

import { palette } from "../../styles";
import {
  type TypographyDecorations,
  type TypographyTypes,
  type TypographyVariants,
  typographyDecorations,
  typographyTypes,
  typographyVariants,
} from "./variants";

type Props = TextProps & {
  center?: boolean;
  shadow?: boolean;
  children: ToDo;
  style?: StyleProp<TextStyle>;
  variant?: TypographyVariants;
  type?: TypographyTypes;
  decoration?: TypographyDecorations;
};

const Typography = ({
  center,
  children,
  style,
  type = "primary",
  variant = "body",
  shadow,
  decoration = "none",
  ...rest
}: Props) => {
  const computedStyles = useMemo(
    () =>
      [
        typographyTypes[type],
        style,
        typographyVariants[variant],
        typographyDecorations[decoration],
        center ? styles.center : {},
        shadow ? styles.textWithShadow : {},
        {
          fontSize:
            StyleSheet.flatten(style)?.fontSize || (typographyVariants[variant].fontSize as number),
        },
        {
          lineHeight:
            StyleSheet.flatten(style)?.lineHeight ||
            (typographyVariants[variant].lineHeight as number),
        },
      ] as StyleProp<TextStyle>,
    [center, shadow, decoration, style, type, variant],
  );

  return (
    <Text {...rest} allowFontScaling={false} accessible style={computedStyles}>
      {children}
    </Text>
  );
};

export default React.memo(Typography);

const styles = StyleSheet.create({
  center: {
    textAlign: "center",
  },
  textWithShadow: Platform.select({
    default: {
      textShadowColor: `${palette.common.black}99`,
      textShadowOffset: { height: -1, width: 0 },
      textShadowRadius: 4,
    },
    web: {},
  }),
});
